import { Flex } from "@chakra-ui/react";
import { t } from "i18next";
import { StripePayment, StripePaymentConfigTypes } from "./StripePayment";
import {
  Release,
  ReleasePricingType,
  ReleaseUserPurchase,
  ServerResponse,
} from "../../../types";
import { FunnelComponentProps } from "../../funnel/types";
import {
  GET_SAVED_PAYMENT_METHODS_QUERY_KEY,
  unlockRelease,
} from "../api/paymentApi";
import { usePaymentMutation } from "../hooks/usePaymentMutation";
import { useUserData } from "../../../hooks/useUserData";
import axios from "axios";
import { useRefetchAccountAndRelease } from "../../../hooks/useRefetchAccountAndRelease";
import { GET_COMIC_RELEASES_QUERY_KEY } from "../../../screens/reader/api/readerScreenApi";
import { PurchaseButton } from "./PurchaseButton";
import { useQueryClient } from "@tanstack/react-query";
import { useReleasePricingInfo } from "../../../hooks/useReleasePricingInfo";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";

export function PurchaseReleaseModalFooter({
  release,
  onClose,
  onSuccess: onSuccessProp,
}: FunnelComponentProps) {
  const { userData } = useUserData();
  const { comic, lang_id } = release || {};
  const isSmallBreakpoint = useIsSmallBreakpoint();

  // Fixed footer when: small screen
  const useFixedFooter = isSmallBreakpoint;

  const refetchQueries = useRefetchAccountAndRelease({
    invalidateUser: false,
    extraKeys: [
      [...GET_COMIC_RELEASES_QUERY_KEY, comic?.id, lang_id],
      ["fetchedComicReleases", comic?.id],
      GET_SAVED_PAYMENT_METHODS_QUERY_KEY,
    ],
  });

  const queryClient = useQueryClient();
  const pricingInfo = useReleasePricingInfo(release as Release);

  const { type, pricePointToUse } = pricingInfo;

  const pricePoint = pricePointToUse ?? release?.ppb_price_point ?? 1;

  const price = pricePoint / 100;

  const isAGoldPricePoint =
    type === ReleasePricingType.GoldDiscount ||
    type === ReleasePricingType.OnGoldSale;

  const onSuccess = async () => {
    setWaiting(true);
    const data = await refetchQueries();

    // Update feature card cache directly with purchase data. This is done in order to
    // immediately reflect the purchase in the UI without needing to refetch the card.
    // This is useful for ALC releases where the card may not be refetched immediately
    // const releaseCacheKey = `${release?.entity_type}-${release?.id}`;

    const releaseCacheKey = `${release?.entity_type}-${release?.id}`;
    queryClient.setQueryData([releaseCacheKey], (releaseData: Release) => {
      if (!releaseData) return releaseData;

      const purchaseData = {
        entity_type: "ReleaseUserPurchase",
        id: Date.now(),
        is_active: 1,
        release_id: release?.id,
        user_id: userData?.id,
        create_time: new Date(),
      } as ReleaseUserPurchase;

      return { ...releaseData, release_user_purchase: purchaseData };
    });

    if (onSuccessProp) {
      onSuccessProp({ data: [data[0], data[1]] });
    } else {
      onClose?.();
    }
  };

  const mutationFn = (stripePaymentConfig: StripePaymentConfigTypes) => {
    if (!release) return Promise.resolve();
    return unlockRelease(release.id, {
      price_point_id: pricePoint,
      is_gold: isAGoldPricePoint,
      raw_price: pricePoint / 100,
      stripeToken: stripePaymentConfig.stripeToken,
      ownerPaymentServiceTokenId:
        stripePaymentConfig.ownerPaymentServiceTokenId,
      full_price: release.ppb_price_point
        ? parseFloat((release.ppb_price_point / 100).toFixed(2))
        : undefined,
      is_sale: pricingInfo.isOnSale,
    });
  };

  const onError = (error: Error) => {
    let errorMessage = t("components.payments.purchaseRelease.purchaseDecline");
    if (axios.isAxiosError<ServerResponse<string>>(error)) {
      errorMessage = error.response?.data?.payload.results || errorMessage;
    }

    return errorMessage;
  };

  const {
    mutate: purchaseRelease,
    failedCard,
    cardValidationError,
    waiting,
    setWaiting,
  } = usePaymentMutation(mutationFn, onSuccess, onError);

  return (
    <Flex flexDirection="column" width="100%" gap="24px">
      {release?.release_user_purchase ? (
        <PurchaseButton
          text={
            release?.user_read_status ? "Continue reading" : "Start reading"
          }
          onClick={() => onSuccessProp({})}
        />
      ) : (
        <StripePayment
          waiting={waiting}
          completePurchase={purchaseRelease}
          submitButtonText={`${t("components.payments.pdf.purchaseReleaseFor")} $${price}`}
          responseError={cardValidationError}
          failedCard={failedCard}
          paddingBottom="16px"
          useFixedFooter={useFixedFooter}
        />
      )}
    </Flex>
  );
}
